import { useMiningStore } from '../../../Store/miningStore';
import { useMiningTime } from './useMiningTime';
import { useMiningRewards } from './useMiningRewards';
import { useCallback, useMemo, useState, useRef, useEffect } from 'react';

export const useMiningStatus = () => {
  const miningData = useMiningStore((state) => state.miningData);
  const updateMiningStatus = useMiningStore((state) => state.updateMiningStatus);
  const { convertToUserTimezone, useTimeUpdate } = useMiningTime();
  const { calculateRewards } = useMiningRewards();
  
  const [timeLeft, setTimeLeft] = useState<string>('');
  const [rewards, setRewards] = useState({
    currentDC: 0,
    currentUSDT: 0,
    totalDC: 0,
    totalUSDT: 0,
    percentageComplete: 0
  });

  const miningInfo = useMemo(() => ({
    status: miningData?.mining.status,
    dateStart: miningData?.mining.date_start,
    dateEnd: miningData?.mining.date_end,
    originalDateEnd: miningData?.original_date_end,
    amount: miningData?.mining.amount,
    amountUsdt: miningData?.mining.amount_usdt
  }), [miningData]);

  useEffect(() => {
    if (miningInfo.status === 'process' && miningInfo.dateEnd) {
      const endTime = new Date(miningInfo.dateEnd + 'Z').getTime();
      const now = Date.now();
      
      if (now >= endTime) {
        updateMiningStatus('claim');
        setTimeLeft('0 мин. 0 с.');
        if (miningInfo.amount !== undefined && miningInfo.amountUsdt !== undefined) {
          setRewards({
            currentDC: Number(miningInfo.amount.toFixed(3)),
            currentUSDT: Number(miningInfo.amountUsdt.toFixed(4)),
            totalDC: miningInfo.amount,
            totalUSDT: miningInfo.amountUsdt,
            percentageComplete: 100
          });
        }
      }
    }
  }, [miningInfo.status, miningInfo.dateEnd, miningInfo.amount, miningInfo.amountUsdt, updateMiningStatus]);

  useEffect(() => {
    if (!miningInfo.status || miningInfo.amount === undefined || miningInfo.amountUsdt === undefined) return;

    if (miningInfo.status === 'claim' || miningInfo.status === 'done') {
      setRewards({
        currentDC: Number(miningInfo.amount.toFixed(3)),
        currentUSDT: Number(miningInfo.amountUsdt.toFixed(4)),
        totalDC: miningInfo.amount,
        totalUSDT: miningInfo.amountUsdt,
        percentageComplete: 100
      });
      setTimeLeft('0 мин. 0 с.');
    }
  }, [miningInfo.status, miningInfo.amount, miningInfo.amountUsdt]);

  const lastUpdateRef = useRef<number>(0);

  const updateTimeAndRewards = useCallback((timeInfo: { text: string; updateInterval: number; isCompleted: boolean }) => {
    const now = Date.now();
    const shouldUpdate = now - lastUpdateRef.current >= timeInfo.updateInterval;
    
    setTimeLeft(timeInfo.text);

    if (shouldUpdate) {
      lastUpdateRef.current = now;

      if (miningInfo.status === 'process' &&
          miningInfo.dateStart &&
          miningInfo.dateEnd &&
          miningInfo.originalDateEnd &&
          miningInfo.amount !== undefined &&
          miningInfo.amountUsdt !== undefined
      ) {
        const newRewards = calculateRewards(
          miningInfo.dateStart,
          miningInfo.dateEnd,
          miningInfo.originalDateEnd,
          miningInfo.amount,
          miningInfo.amountUsdt,
          miningInfo.status
        );
        setRewards(newRewards);
      }
    }
  }, [miningInfo, calculateRewards]);

  useTimeUpdate(
    miningInfo.status === 'process' ? miningInfo.dateEnd : undefined,
    updateTimeAndRewards
  );

  const statusText = useMemo(() => {
    switch (miningInfo.status) {
      case 'process':
        return 'Идет майнинг';
      case 'claim':
        return 'Собрать майнинг';
      default:
        return 'Начать майнинг';
    }
  }, [miningInfo.status]);

  const formattedDates = useMemo(() => ({
    formattedStart: miningInfo.dateStart ? convertToUserTimezone(miningInfo.dateStart) : null,
    formattedEnd: miningInfo.dateEnd ? convertToUserTimezone(miningInfo.dateEnd) : null,
    formattedOriginalEnd: miningInfo.originalDateEnd ? convertToUserTimezone(miningInfo.originalDateEnd) : null,
  }), [miningInfo.dateStart, miningInfo.dateEnd, miningInfo.originalDateEnd, convertToUserTimezone]);

  return {
    miningStatus: miningInfo.status,
    statusText,
    timeLeft,
    rewards,
    ...formattedDates
  };
};